import {
  columnSizingFeature,
  columnVisibilityFeature,
  createSortedRowModel,
  flexRender,
  rowSortingFeature,
  sortFn_alphanumeric as sortFnAlphanumeric,
  sortFn_alphanumericCaseSensitive as sortFnAlphanumericCaseSensitive,
  sortFn_basic as sortFnBasic,
  sortFn_datetime as sortFnDatetime,
  sortFn_text as sortFnText,
  sortFn_textCaseSensitive as sortFnTextCaseSensitive,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import { isString, get } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { FixedSizeList } from 'react-window';
import styled, { css } from 'styled-components';

import { Text, SortZa, SortAz, Icon } from '@summer/ui-components';
import {
  V7_ROW_PROPS,
  adaptCellRenderer,
  toColumnDefs,
  toColumnVisibility,
  toHiddenHeaderIds,
} from '@summer/ui-components/src/core/CardTable/columnAdapter';
import { hexToRgba } from '@summer/ui-components/src/helpers/utils/common';

import { Flex } from 'components/box/Box';
import { COLORS } from 'styles/styleguide';

import type {
  ColumnDef,
  ColumnVisibilityState,
  Row,
  SortingState,
} from '@tanstack/react-table';
import type { V7Column } from '@summer/ui-components/src/core/CardTable/columnAdapter';
import type { CSSProperties } from 'react';

const GUTTER_SIZE = 4;
const ITEM_SIZE = 84;

type TableRow = Record<string, unknown>;

interface RowRenderProps {
  index: number;
  style: CSSProperties;
}

interface SortableCardTableProps {
  columns: V7Column[];
  data: TableRow[];
  getCustomRowProps?: (
    props: typeof V7_ROW_PROPS,
    meta: { row: Row<typeof features, TableRow> },
  ) => Record<string, unknown>;
  tableIndex?: number;
  tableName?: string;
  colDefaultWidth?: number;
  align?: 'center' | 'left' | 'right';
  fitToContent?: boolean;
  style?: CSSProperties | null;
  horizontalScroll?: boolean;
  virtualizedOptions?: {
    height?: number;
    ref?: unknown;
    onItemsRendered?: (props: Record<string, number>) => void;
  };
}

// v8 has no layout plugin, so reproduce the sizing useFlexLayout used to emit
const sizeStyle = (size: number): CSSProperties => ({
  boxSizing: 'border-box',
  flex: `${String(size)} 0 auto`,
  minWidth: '0px',
  width: `${String(size)}px`,
});

const DefaultRowCellComponent = ({ cell }: { cell: { value: unknown } }) => {
  return (
    <Text
      weight="medium"
      color="dark"
      size="small"
      truncate
      paragraph
      smallTracking
    >
      {cell.value as string}
    </Text>
  );
};

const features = tableFeatures({
  columnSizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  sortFns: {
    alphanumeric: sortFnAlphanumeric,
    alphanumericCaseSensitive: sortFnAlphanumericCaseSensitive,
    basic: sortFnBasic,
    datetime: sortFnDatetime,
    text: sortFnText,
    textCaseSensitive: sortFnTextCaseSensitive,
  },
  sortedRowModel: createSortedRowModel(),
});

const SortableCardTable = ({
  columns,
  data,
  getCustomRowProps,
  tableIndex = 0,
  tableName,
  colDefaultWidth = 100,
  align = 'center',
  fitToContent = false,
  style = null,
  horizontalScroll,
  virtualizedOptions,
}: SortableCardTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const columnDefs = useMemo(
    () => toColumnDefs(columns) as Array<ColumnDef<typeof features, TableRow>>,
    [columns],
  );
  const columnVisibility = useMemo(
    () => toColumnVisibility(columns) as ColumnVisibilityState,
    [columns],
  );
  const hiddenHeaderIds = useMemo(() => toHiddenHeaderIds(columns), [columns]);
  const defaultColumn = useMemo(
    () => ({
      size: colDefaultWidth,
      cell: adaptCellRenderer(DefaultRowCellComponent),
      // v7 compared raw values; v8 would otherwise lowercase both sides
      sortFn: 'basic' as const,
    }),
    [colDefaultWidth],
  );

  const table = useTable({
    features,
    data,
    columns: columnDefs,
    defaultColumn,
    state: { columnVisibility, sorting },
    onSortingChange: setSorting,
    enableSortingRemoval: false,
  });

  const rows = table.getRowModel().rows;

  const RenderRow = useCallback(
    ({ index, style: rowStyle }: RowRenderProps) => {
      const row = rows[index];
      const rowId = `${tableName ?? 'table'}${String(tableIndex)}-row${String(index)}`;
      const rowProps = getCustomRowProps
        ? { ...V7_ROW_PROPS, ...getCustomRowProps(V7_ROW_PROPS, { row }) }
        : V7_ROW_PROPS;

      return (
        <Row
          {...rowProps}
          style={{
            ...rowStyle,
            top: Number(rowStyle.top) + GUTTER_SIZE,
            height: Number(rowStyle.height) - GUTTER_SIZE,
            left: Number(rowStyle.left) + GUTTER_SIZE,
            width: `calc(${String(rowStyle.width)} - ${String(GUTTER_SIZE * 2)}px)`,
            ...(rowProps.style as CSSProperties),
          }}
          tabIndex="0"
          className="table-row"
          id={rowId}
        >
          {row.getVisibleCells().map(cell => (
            <RowCell
              key={cell.id}
              role="cell"
              align={align}
              style={sizeStyle(cell.column.getSize())}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </RowCell>
          ))}
        </Row>
      );
    },
    [rows, align, getCustomRowProps, tableIndex, tableName],
  );

  return (
    <Container
      className="card-table"
      style={style ?? undefined}
      horizontalScroll={horizontalScroll}
    >
      <Table role="table" data-private fitToContent={fitToContent}>
        {table.getHeaderGroups().map(headerGroup => (
          <HeaderContainer role="row" key={headerGroup.id}>
            {headerGroup.headers.map(header => {
              const { header: headerDef } = header.column.columnDef;
              const headerStyle = {
                ...sizeStyle(header.getSize()),
                ...(hiddenHeaderIds.has(header.column.id)
                  ? { display: 'none' }
                  : {}),
              };
              const label = isString(headerDef) ? (
                <Text weight="medium" size="extraSmall">
                  {flexRender(headerDef, header.getContext())}
                </Text>
              ) : (
                flexRender(headerDef, header.getContext())
              );

              return header.column.getCanSort() ? (
                <SortableHeaderCell
                  key={header.id}
                  role="columnheader"
                  colSpan={header.colSpan}
                  align={align}
                  style={headerStyle}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {label}
                  <SortIconContainer>
                    <SortIcon
                      fill="#6D7581"
                      SvgComponent={
                        header.column.getIsSorted() === 'desc' ? SortZa : SortAz
                      }
                    />
                  </SortIconContainer>
                </SortableHeaderCell>
              ) : (
                <HeaderCell
                  key={header.id}
                  role="columnheader"
                  colSpan={header.colSpan}
                  align={align}
                  style={headerStyle}
                >
                  {label}
                </HeaderCell>
              );
            })}
          </HeaderContainer>
        ))}
        <BodyContainer>
          <BodyContainer>
            <FixedSizeList
              height={
                data.length < 4
                  ? data.length * ITEM_SIZE + 30
                  : get(virtualizedOptions, 'height', 0)
              }
              ref={get(virtualizedOptions, 'ref')}
              onItemsRendered={get(virtualizedOptions, 'onItemsRendered')}
              itemCount={rows.length}
              itemSize={ITEM_SIZE + GUTTER_SIZE}
            >
              {RenderRow}
            </FixedSizeList>
          </BodyContainer>
        </BodyContainer>
      </Table>
    </Container>
  );
};

type Aligned = { align?: SortableCardTableProps['align'] };

// colSpan reproduces the header markup react-table v7 emitted; a div does not declare it
type AlignedHeader = Aligned & { colSpan?: number };

const Container = styled.div<{ horizontalScroll?: boolean }>`
  width: 100%;
  overflow: scroll;
  min-width: ${({ horizontalScroll }) => (horizontalScroll ? '150vw' : 'unset')};
`;

const Table = styled.div<{ fitToContent?: boolean }>`
  width: ${({ fitToContent }) => (fitToContent ? 'fit-content' : '100%')};
  min-width: unset !important;
  padding: 0px;
`;

const HeaderContainer = styled.div`
  display: flex;
  width: 100%;
  height: 48px;
  padding: 16px 16px;
  align-items: center;
  border-radius: 3px;
  background-blend-mode: multiply;
  background-color: ${hexToRgba(COLORS.azure, 0.09)};
`;

const BodyContainer = styled.div`
  & > div {
    margin-bottom: 8px;
  }
`;

const rowInteractiveStyle = css`
  cursor: pointer;

  :hover {
    box-shadow: 0 2px 10px 2px rgba(214, 221, 231, 0.81);
    border: solid 1px ${COLORS.lighterGrey};
    background-color: ${COLORS.white};
  }

  :active {
    box-shadow: 0 2px 10px 2px rgba(214, 221, 231, 0.81);
    border: solid 1px ${COLORS.lighterGrey};
    background-color: ${COLORS.lightestGrey};
  }

  :focus {
    box-shadow: 0 0 5px 0 rgba(0, 166, 255, 0.5);
    border: solid 1px #41beff;
    outline: none;
  }
`;

const rowValidStyle = css`
  border-left: solid 6px ${COLORS.orange} !important;
  &.error-focus {
    box-shadow: 0 2px 10px 2px rgba(214, 221, 231, 0.81);
    border: solid 1px ${COLORS.lighterGrey};
    background-color: ${COLORS.white};
  }
`;

const Row = styled.div.attrs(props => ({
  tabIndex: props.onClick ? '0' : '-1',
}))`
  display: inline-flex;
  width: 100%;
  justify-content: center;
  padding: 16px 16px;
  border-radius: 6px;
  border: solid 1px #ebeff4;
  background-color: ${COLORS.white};
  transition: all 0.3s ease;
  outline: none;
  font-size: 14px !important;

  :first-child {
    text-align: left;
  }

  ${({ onClick }) => onClick && rowInteractiveStyle}

  ${({ valid }) => valid === false && rowValidStyle}
`;

/*
 * The use of justify-content in this component it to support our current web-app HeaderCell component
 * when using HeaderCell component align will not work and without it tooltip will break
 *
 * Todo: update tooltip design and HeaderCell to support normal align
 * */
const HeaderCell = styled.div<AlignedHeader>`
  flex-shrink: 1 !important;
  flex-flow: nowrap;
  overflow: hidden;
  padding: 8px 8px;
  text-align: ${({ align }) => align};

  > * {
    justify-content: center;
  }

  :first-child {
    text-align: left;

    > * {
      justify-content: flex-start;
    }
  }
`;

const SortableHeaderCell = styled.div<AlignedHeader>`
  flex-shrink: 1 !important;
  flex-flow: nowrap;
  overflow: hidden;
  display: flex;
  align-items: center;
  padding: 8px 8px;
  display: flex;
  text-align: ${({ align }) => align};
`;

const RowCell = styled.div<Aligned>`
  flex-flow: row nowrap;
  flex-shrink: 1 !important;
  flex-basis: auto;
  overflow: hidden;
  padding: 8px 8px;
  text-align: ${({ align }) => align};

  :first-child {
    text-align: left;
  }
  font-size: 14px !important;
`;

const SortIcon = styled(Icon)`
  cursor: pointer;
`;

const SortIconContainer = styled(Flex)`
  cursor: pointer;
  width: 21px;
  height: 21px;
  margin-left: 8px;
  justify-content: center;
  align-items: center;
  &:hover {
    mix-blend-mode: multiply;
    border-radius: 3px;
    background: ${COLORS.blueGrey};
  }
`;

SortableCardTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
        .isRequired,
      id: PropTypes.string,
      columns: PropTypes.array,
      Header: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
        PropTypes.elementType,
      ]),
      Cell: PropTypes.oneOfType([PropTypes.func, PropTypes.elementType]),
      width: PropTypes.number,
      minWidth: PropTypes.number,
      maxWidth: PropTypes.number,
    }),
  ).isRequired,
  getRowProps: PropTypes.func,
  tableIndex: PropTypes.number,
  colDefaultWidth: PropTypes.number,
  fitToContent: PropTypes.bool,
  align: PropTypes.PropTypes.oneOf(['center', 'left', 'right']),
};

export default SortableCardTable;
